/* eslint-disable no-restricted-imports */
import { CopyButton } from '@components/Copy';
import {
  AlertActionButton,
  AlertDismissButton,
  SystemAlertHeader,
} from '@components/SystemAlerts/shared/components';
import {
  AlertTypeProps,
  SystemAlertType,
} from '@components/SystemAlerts/shared/constants';
import { newColors } from '@components/Theme/colors';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useFlagappAllowDebug } from '@generated/flags/app-allow-debug';
import { FS_UNMASK } from '@utils/fullstory';
import { clamp } from 'lodash-es';
import { toast } from 'react-toastify';
import { ToastOptions } from '..';

// The toast container renders at `z-index: 200000 !important` (see
// src/app/Styles/index.tsx). The copy button's "Copied!" tooltip must render
// above it, otherwise it is hidden behind the toast.
const TOAST_TOOLTIP_Z_INDEX = 200001;

const ToastContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Content = styled.div<AlertTypeProps>((props) => ({
  color: props?.color ? props.color : newColors.system[props.type].text,
  margin: '8px 0 16px 24px',
}));

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 24px;
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface ToastProps {
  title?: string;
  content: Maybe<string>;
  action?: {
    label: string;
    onClick: () => void;
  };
  type: SystemAlertType;
  closeToast?: () => void;
  toastOptions?: ToastOptions;
  unmaskTitle?: boolean;
  unmaskContent?: boolean;
}

const Toast = ({
  title,
  content,
  action,
  closeToast,
  type,
  unmaskTitle = false,
  unmaskContent = false,
}: ToastProps): JSX.Element => {
  const { system } = useTheme();
  const toastColors = system.toast[type];
  const isAllowDebugEnabled = useFlagappAllowDebug();
  const showCopyButton =
    isAllowDebugEnabled &&
    (type === 'warning' || type === 'error') &&
    Boolean(content);
  return (
    <ToastContainer>
      <SystemAlertHeader
        title={title}
        type={type}
        unmaskTitle={unmaskTitle}
        colors={{ accent: toastColors.accent, title: toastColors.title }}
      />
      <Content
        type={type}
        className={unmaskContent ? FS_UNMASK : undefined}
        color={toastColors.text}
      >
        {content}
        {showCopyButton && (
          // The toast is configured with `closeOnClick: true`, so a click that
          // bubbles up to the toast body dismisses (unmounts) the toast before
          // the "Copied!" tooltip can show. Stop propagation so copying keeps
          // the toast open. The wrapper is non-interactive; the CopyButton
          // itself remains keyboard accessible.
          // eslint-disable-next-line mastery/no-onclick-on-html-elements
          <span
            onClick={(e): void => e.stopPropagation()}
            css={{ display: 'inline-flex', verticalAlign: 'middle' }}
          >
            <CopyButton
              name="copy"
              data-testid="toast-copy-button"
              value={[title, content].filter(Boolean).join('\n')}
              // The toast container renders at a very high z-index; raise the
              // tooltip above it so the "Copied!" popup is visible.
              tooltipStyle={{ zIndex: TOAST_TOOLTIP_Z_INDEX }}
              css={{
                button: {
                  background: `${toastColors.background} !important`,
                  boxShadow: 'none !important',
                  border: 'none !important',
                },
                'button:hover:not(:disabled), button:active:not(:disabled), button:focus:not(:disabled), button:focus-visible:not(:disabled)':
                  {
                    background: `${toastColors.background} !important`,
                    boxShadow: 'none !important',
                    border: 'none !important',
                  },
                // Match the toast background in dark mode too, overriding the
                // Button dark-mode remap (ME-449354) with the same ancestor scope.
                ['html.theme-dark.button-dark-mode-enabled & button:hover:not(:disabled), html.theme-dark.button-dark-mode-enabled & button:active:not(:disabled), html.theme-dark.button-dark-mode-enabled & button:focus:not(:disabled), html.theme-dark.button-dark-mode-enabled & button:focus-visible:not(:disabled)']:
                  {
                    background: `${toastColors.background} !important`,
                    boxShadow: 'none !important',
                    border: 'none !important',
                  },
                svg: { color: `${toastColors.text} !important` },
              }}
            />
          </span>
        )}
      </Content>
      <ButtonContainer>
        {action && (
          <AlertActionButton
            action={action}
            type={type}
            css={{
              background: toastColors.actionButtonBackground,
              color: toastColors.actionButtonColor,
              ':hover': {
                background: `${toastColors.actionButtonHover} !important`,
              },
            }}
          />
        )}
        <RightActions>
          <AlertDismissButton
            type={type}
            data-testid="toast-dismiss-button"
            onClick={(): void => closeToast?.()}
            css={{
              background: 'transparent',
              color: toastColors.dismissButtonColor,
              ':hover': {
                background: `${toastColors.dismissButtonHover} !important`,
              },
            }}
          />
        </RightActions>
      </ButtonContainer>
    </ToastContainer>
  );
};

// ts-unused-exports:disable-next-line
export interface ToasterProps {
  title?: string;
  content: Maybe<string>;
  action?: {
    label: string;
    onClick: () => void;
  };
  type: 'success' | 'warning' | 'error' | 'info';
  toastOptions?: ToastOptions;
  unmaskTitle?: boolean;
  unmaskContent?: boolean;
}

export const toaster = ({
  title,
  content,
  action,
  type,
  unmaskTitle,
  unmaskContent,
  toastOptions,
}: ToasterProps): number | string => {
  const getToastTime = (): number => {
    const trimTitle = title?.replace(/\s/g, '') ?? '';
    const trimContent = content?.replace(/\s/g, '') ?? '';
    const chars = trimTitle.concat(trimContent);
    const charCount = chars?.length;
    const time = charCount && charCount * 50;
    const showTime = time && clamp(time, 5000, 10000);

    if (showTime) {
      return showTime;
    }
    return 5000;
  };

  return toast[type](
    <Toast
      title={title}
      content={content}
      action={action}
      type={type}
      unmaskTitle={unmaskTitle}
      unmaskContent={unmaskContent}
    />,
    {
      autoClose: getToastTime(),
      hideProgressBar: true,
      // We render our own type icon inside <Toast>; disable react-toastify's
      // default type-based icon (added in v9+) to avoid a duplicate icon.
      icon: false,
      containerId: 'toast',
      ...toastOptions,
    },
  );
};
